// The frame of the signed-in pages: header (logo, organization, user, language, sign out), the
// verification, suspension and subscription banners, a tab bar, the plan strip, the page, "Manage cookies".
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { BillingState, UsageSummary } from '../types';
import { ManageCookies } from './CookieBanner';
import { PlanStrip } from './PlanStrip';
import { Alert, BackToSite, Button, LanguageToggle } from './ui';

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user, signOut, setLocale } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [resent, setResent] = useState<'idle' | 'sent' | 'error' | 'limited'>('idle');
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const orgId = user?.organization?.id;
  const plan = user?.organization?.plan_name;
  useEffect(() => {
    if (!orgId) return;
    api
      .subscription()
      .then(setBilling)
      .catch(() => setBilling(null));
  }, [orgId, plan]);
  // Re-read on every page change, so the strip follows a purchase or a cap change made on another page.
  useEffect(() => {
    if (!orgId) return;
    api
      .usage()
      .then(setUsage)
      .catch(() => setUsage(null));
  }, [orgId, plan, location.pathname]);
  const billingReason = billing?.reason ?? null;
  if (!user) return null;

  const resend = async () => {
    try {
      await api.resendVerification();
      setResent('sent');
    } catch (err) {
      setResent(err instanceof ApiError && err.is('rate_limited') ? 'limited' : 'error');
    }
  };

  const tabs = [
    { to: '/', label: t('nav.account'), end: true },
    { to: '/organization', label: t('nav.organization') },
    { to: '/usage', label: t('nav.usage') },
    { to: '/billing', label: t('nav.billing') },
    { to: '/invoices', label: t('nav.invoices') },
  ];
  const orgStatus = user.organization?.status;

  return (
    <div className="flex min-h-screen flex-col bg-pane">
      <header className="border-b border-line-soft bg-white">
        <div className="mx-auto flex max-w-[960px] items-center gap-4 px-4 py-2.5">
          <BackToSite />
          <NavLink to="/" className="flex items-center gap-2">
            <img src="/brand/sculpt-agent-icon.svg" alt="" className="h-6 w-6" draggable={false} />
            <span className="text-[14px] font-medium tracking-[-0.01em] text-ink">Sculpt</span>
          </NavLink>
          {user.organization && <span className="hidden truncate text-[13px] text-muted sm:inline">· {user.organization.name}</span>}
          <span className="flex-1" />
          <span className="hidden max-w-[220px] truncate font-mono text-[11px] text-faint md:inline" title={user.email}>
            {user.email}
          </span>
          <LanguageToggle onChange={(l) => void setLocale(l)} />
          <Button
            variant="ghost"
            className="px-2! py-1! text-[12px]"
            onClick={() => {
              void signOut().then(() => navigate('/login'));
            }}
          >
            {t('app.signOut')}
          </Button>
        </div>
        <nav className="mx-auto flex max-w-[960px] gap-1 overflow-x-auto px-2">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `whitespace-nowrap border-b-2 px-2 py-2 text-[13px] ${isActive ? 'border-accent font-medium text-ink' : 'border-transparent text-muted hover:text-ink'}`}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto flex w-full max-w-[960px] flex-1 flex-col gap-4 px-4 py-5">
        {!user.email_verified && (
          <Alert kind="warn">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{resent === 'sent' ? t('banner.resent') : t('banner.unverified', { email: user.email })}</span>
              {resent !== 'sent' && (
                <Button variant="secondary" className="py-1! text-[12px]" onClick={() => void resend()}>
                  {t('banner.resend')}
                </Button>
              )}
            </div>
            {resent === 'limited' && <div className="mt-1 text-[12px]">{t('app.rateLimited')}</div>}
            {resent === 'error' && <div className="mt-1 text-[12px]">{t('app.error')}</div>}
          </Alert>
        )}
        {orgStatus && orgStatus !== 'active' && <Alert kind="error">{orgStatus === 'archived' ? t('banner.archived') : t('banner.suspended')}</Alert>}
        {billingReason && location.pathname !== '/billing' && (
          <Alert kind={billingReason === 'payment_overdue' ? 'error' : 'warn'}>
            {t(`billing.reason.${billingReason}`)}{' '}
            <Link to="/billing" className="underline">
              {t('banner.goBilling')}
            </Link>
          </Alert>
        )}
        {usage && location.pathname !== '/usage' && <PlanStrip usage={usage} admin={user.organization?.role === 'owner' || user.organization?.role === 'admin'} />}
        <Outlet context={{ locale: i18n.language }} />
        <ManageCookies className="mt-auto pt-6" />
      </main>
    </div>
  );
}
